import { FC, useEffect, useMemo, useRef, useState } from 'react'
import gql from 'graphql-tag'
import styled from 'styled-components'
import { Close } from 'svgIcons'
import {
  useGqlNewsItemModifyModalLazyQuery,
  useUpdateNewsItemMutation,
} from 'generated/graphql'
import Button from '@app.components/Button'
import NewsEditor, {
  IFEmbedContent,
  NewsEditorRef,
} from '@app.components/Editor/NewsEditor'
import NewsEditorLoading from '@app.components/Editor/NewsEditorLoading'
import ConfirmCloseModal from '@app.components/Editor/components/ConfirmCloseModal'
import Modal from '@app.components/Modal/Modal'
import { parseHashtagFromContentHtml } from '@app.modules/util'

interface IFNewsItemModifyModalProps {
  newsItemId: string
  onClose: () => void
}

gql`
  fragment NewsItemModifyModal_attachment on Attachment {
    id
    url
  }

  fragment NewsItemModifyModal_attachmentConnection on AttachmentConnection {
    totalCount
    nodes {
      ...NewsItemModifyModal_attachment
    }
  }

  fragment NewsItemModifyModal_embed on Embed {
    id
    description
    domain
    url
    imageUrl
  }

  fragment NewsItemModifyModal_newsItem on NewsItem {
    id
    content
    images {
      ...NewsItemModifyModal_attachmentConnection
    }
    embed {
      ...NewsItemModifyModal_embed
    }
  }

  query gqlNewsItemModifyModal($newsItemId: ID!) {
    newsItem(id: $newsItemId) {
      ...NewsItemModifyModal_newsItem
    }
  }
`

const NewsItemModifyModal: FC<IFNewsItemModifyModalProps> = ({
  newsItemId,
  onClose,
}) => {
  const editorRef = useRef<NewsEditorRef>(null)

  const [isCanSubmit, setIsCanSubmit] = useState<boolean>(false)
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const [closeModalVisible, setCloseModalVisible] = useState<boolean>(false)

  const [getNewsItem, { data, loading }] = useGqlNewsItemModifyModalLazyQuery()

  const [updateNewsItem] = useUpdateNewsItemMutation({
    onCompleted: () => {
      onClose()
    },
  })

  const embedData: IFEmbedContent | undefined = useMemo(() => {
    const embed = data?.newsItem?.embed
    if (!embed) {
      return undefined
    }

    return {
      url: embed.url,
      description: embed.description,
      domain: embed.domain,
      imageUrl: embed.imageUrl,
    } as IFEmbedContent
  }, [data?.newsItem?.embed])

  const handleSubmit = () => {
    const content = (editorRef.current?.getValue() || '') as string
    const imageIds = editorRef.current?.uploadedImageAttachmentIds || []
    const tagNames = parseHashtagFromContentHtml(content)

    updateNewsItem({
      variables: {
        input: {
          id: newsItemId,
          content,
          imageIds,
          tagNames,
        },
      },
    })
  }

  const handleClose = () => {
    if (isDirty) {
      setCloseModalVisible(true)
    } else {
      onClose()
    }
  }

  useEffect(() => {
    getNewsItem({
      variables: {
        newsItemId,
      },
    })
  }, [newsItemId, getNewsItem])

  return (
    <Modal onClose={handleClose} open>
      <StyledWrapper id={`news-item-modify-modal-${newsItemId}`}>
        <div className="recommend-header">
          <div>소식 수정</div>
          <Close className="close-button" onClick={handleClose} />
        </div>
        <div className="recommend-content">
          {data?.newsItem?.content && (
            <NewsEditor
              ref={editorRef}
              setIsCanSubmit={setIsCanSubmit}
              setIsDirty={setIsDirty}
              initialContent={data.newsItem.content}
              initialEmbed={embedData}
              initialImages={data.newsItem.images.nodes.map((node) => {
                return {
                  attachmentID: node?.id || '',
                  url: node?.url || '',
                  name: '',
                  size: 0,
                  type: '',
                }
              })}
              isModify
            />
          )}
          {loading && <NewsEditorLoading />}
        </div>
        <div className="recommend-footer">
          <Button
            onClick={handleSubmit}
            disabled={!isCanSubmit}
            className="button"
          >
            등록
          </Button>
        </div>
        {closeModalVisible && (
          <ConfirmCloseModal
            onConfirm={onClose}
            onClose={() => setCloseModalVisible(false)}
          />
        )}
      </StyledWrapper>
    </Modal>
  )
}

export default NewsItemModifyModal

const StyledWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #fff;
  border-radius: 15px;

  .recommend-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    font-size: 24px;
    font-weight: 700;
    border-bottom: 1px solid #dddddd;
  }

  .close-button {
    position: absolute;
    top: 32px;
    right: 32px;
    width: 16px;
    height: 16px;
    :hover {
      cursor: pointer;
    }
  }

  .recommend-content {
    padding: 24px 0 24px 40px;
    min-height: 294px;
    max-height: 600px;
  }

  .recommend-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 1px solid #ddd;
    padding: 20px 32px 32px;
    .button {
      width: 87px;
      height: 48px;
      font-size: 16px;
      color: #fff;
    }
  }
`
